/**
 * TypeScript interfaces for Meeting Auto-Save functionality
 * 
 * These interfaces define the structure for tracking meeting changes separately 
 * from the main editing state to prevent infinite loops while maintaining 
 * consistency with existing auto-save patterns in the codebase.
 */

/**
 * Core meeting data structure for auto-save tracking
 * This matches the fields that are actually editable in the EditMeetingModal
 */
export interface MeetingAutoSaveData {
  /** Meeting summary title */
  summary_title: string;
  /** Meeting description (HTML content from TinyEditor) */
  description: string;
  /** Meeting date in YYYY-MM-DD format */
  date: string | null;
}

/**
 * Auto-save state container for meeting data
 * Follows the pattern established in useBoardGovernanceAutoSave and other auto-save hooks
 */
export interface MeetingAutoSaveState {
  /** Flag indicating if there are unsaved changes */
  hasChanges: boolean;
  /** Timestamp of last modification */
  lastModified: Date;
  /** The actual meeting data being tracked for auto-save */
  autoSaveData: MeetingAutoSaveData;
}

/**
 * Parameters for the meeting auto-save hook
 * Following the established pattern from other auto-save hooks in the codebase
 */
export interface UseMeetingAutoSaveParams {
  /** Meeting ID for generating unique page keys */
  meetingId: string | null;
  /** Modal visibility state */
  modalVisible: boolean;
  /** Original meeting data for comparison */
  editingMeeting: any | null;
  /** Setter function to update editingMeeting state (for restoring auto-save data) */
  setEditingMeeting: (meeting: any | null) => void;
}

/**
 * Return type for the meeting auto-save hook
 * Provides handlers and state following the established auto-save pattern
 */
export interface UseMeetingAutoSaveReturn {
  /** Auto-save modal visibility state */
  autoSaveVisible: boolean;
  /** Page key used for auto-save storage */
  meetingPageKey: string;
  /** Current auto-save data state */
  autoSaveData: MeetingAutoSaveData;
  /** Callback to handle data changes from the modal */
  onDataChange: (data: Partial<MeetingAutoSaveData>) => void;
  /** Handler to check for auto-save before opening modal (accepts optional meetingId override for async state) */
  handleMeetingModalEdit: (originalOpenHandler: () => void, meetingIdOverride?: string | null) => void;
  /** Handler to discard auto-save and open modal */
  handleAutoSaveCancel: (originalOpenHandler: () => void) => void;
  /** Handler to restore from auto-save */
  handleAutoSaveConfirm: (originalOpenHandler: () => void) => void;
  /** Clear auto-save after successful save */
  clearMeetingModalAutoSave: () => void;
  /** Flag indicating if there are unsaved changes for modal close warning */
  hasUnsavedChanges: boolean;
}

/**
 * Change detection result for comparing meeting data
 * Used internally by the auto-save hook for change tracking
 */
export interface MeetingChangeDetection {
  /** Whether the summary title has changed */
  summaryChanged: boolean;
  /** Whether the description has changed */
  descriptionChanged: boolean;
  /** Whether the date has changed */
  dateChanged: boolean;
  /** Overall change status */
  hasAnyChanges: boolean;
}

/**
 * Default empty meeting auto-save data
 * Used for initialization and resetting state
 */
export const DEFAULT_MEETING_AUTO_SAVE_DATA: MeetingAutoSaveData = {
  summary_title: '',
  description: '',
  date: null,
};

/**
 * Utility type for partial meeting data updates
 * Used in onDataChange callback to allow partial updates
 */
export type PartialMeetingAutoSaveData = Partial<MeetingAutoSaveData>;